import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { CustomTab, CustomMRTTable, CustomDropdown } from "../../components";
import AxiosInstance from "../../services/Api";
import styles from "./plans-pricing/PlansPricing.module.css";

const ALL_PLAN_OPTION = { value: "all", label: "All" };

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: 1, label: "Active" },
  { value: 2, label: "Expired" },
  { value: 3, label: "In Discussion" },
  { value: 0, label: "Inactive" },
];

const STATUS_COLOR_MAP = {
  green: { bg: "#00A63E", text: "#fff" },
  red: { bg: "#E7000B", text: "#fff" },
  blue: { bg: "#039DD9", text: "#fff" },
  orange: { bg: "#F5821F", text: "#fff" },
  grey: { bg: "#9e9e9e", text: "#fff" },
};

const School = () => {
  const navigate = useNavigate();

  const [statusTab, setStatusTab] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(ALL_PLAN_OPTION);
  const [planOptions, setPlanOptions] = useState([ALL_PLAN_OPTION]);
  const [plansLoading, setPlansLoading] = useState(false);

  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [clients, setClients] = useState([]);
  const [totalClients, setTotalClients] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const response = await AxiosInstance.post("/saas/planList", {
        page: 1,
        limit: 100,
        status: "",
      });

      if (response.data?.status && Array.isArray(response.data.data)) {
        const options = response.data.data.map((plan) => ({
          value: plan.plan_id,
          label: plan.plan_name,
        }));
        setPlanOptions([ALL_PLAN_OPTION, ...options]);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
    } finally {
      setPlansLoading(false);
    }
  }, []);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AxiosInstance.post("/saas/list/clients", {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
        status: statusTab,
        plan_id:
          selectedPlan?.value === "all" ? "" : (selectedPlan?.value ?? ""),
        search: searchTerm,
      });

      if (response.data?.status && response.data.data) {
        setClients(response.data.data.list || []);
        setTotalClients(response.data.data.pagination?.total || 0);
      } else {
        setClients([]);
        setTotalClients(0);
        throw new Error(response.data?.message || "Failed to fetch schools");
      }
    } catch (err) {
      console.error("Error fetching schools:", err);
      setError(
        err?.response?.data?.message || err?.message || "An error occurred",
      );
      setClients([]);
      setTotalClients(0);
    } finally {
      setLoading(false);
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    statusTab,
    selectedPlan,
    searchTerm,
  ]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleTabChange = (value) => {
    setStatusTab(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSearchInput("");
    setSearchTerm("");
  };

  const handlePlanChange = (option) => {
    setSelectedPlan(option || ALL_PLAN_OPTION);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleSearchEnter = (value) => {
    setSearchTerm(value || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "client_id",
        header: "Client ID",
        size: 100,
      },
      {
        accessorKey: "plan_name",
        header: "Plan Name",
        size: 160,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "school_name",
        header: "School Name",
        size: 200,
      },
      {
        accessorKey: "school_email",
        header: "School Email",
        size: 220,
      },
      {
        accessorKey: "city",
        header: "City",
        size: 160,
      },
      {
        accessorKey: "admin_name",
        header: "Admin Name",
        size: 150,
      },
      {
        accessorKey: "admin_email",
        header: "Admin Email",
        size: 220,
      },
      {
        accessorKey: "admin_mobile",
        header: "Admin Mobile",
        size: 140,
      },
      // {
      //   accessorKey: "slab_count",
      //   header: "Slab Count",
      //   size: 110,
      //   enableColumnFilter: false,
      //   Cell: ({ cell }) => cell.getValue() ?? "—",
      // },
      {
        accessorKey: "purchase_date",
        header: "Purchase Date",
        size: 130,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "renew_date",
        header: "Renew Date",
        size: 130,
        Cell: ({ cell }) => cell.getValue() || "—",
      },
      {
        accessorKey: "status_label",
        header: "Status",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const label = row.original.status_label || "—";
          const color = row.original.status_color || "grey";
          const { bg, text } = STATUS_COLOR_MAP[color] || STATUS_COLOR_MAP.grey;
          return (
            <span
              style={{
                display: "inline-block",
                padding: "4px 14px",
                borderRadius: "6px",
                fontSize: "13px",
                fontWeight: 600,
                backgroundColor: bg,
                color: text,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
          );
        },
      },
      {
        id: "actions",
        header: "Action",
        size: 80,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <Tooltip title="View" arrow>
            <IconButton
              size="small"
              style={{
                background: "#F5821F",
                color: "#fff",
                borderRadius: "6px",
                padding: "6px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/dashboard/masters/school/view/${row.original.purchase_order_id}`, {
                  state: { school: row.original },
                });
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [navigate],
  );

  return (
    <div>
      <div className={styles.pageHeader}>
        <h1 className={styles.pageTitle}>Schools</h1>
      </div>

      <div className={styles.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "12px",
            marginBottom: "20px",
          }}
        >
          <span style={{ fontSize: "14px", fontWeight: 600, color: "#374151" }}>
            Selected Plan
          </span>
          <div style={{ minWidth: "200px" }}>
            <CustomDropdown
              name="selectedPlan"
              value={selectedPlan}
              onChange={handlePlanChange}
              options={planOptions}
              isClearable={false}
              isLoading={plansLoading}
              size="sm"
              style={{ marginBottom: 0 }}
            />
          </div>
        </div>

        <div className={styles.tabsWrapper}>
          <CustomTab
            tabs={STATUS_TABS}
            value={statusTab}
            onChange={handleTabChange}
          />
        </div>

        {error ? (
          <div
            style={{ color: "#DC2626", fontSize: "14px", marginBottom: "16px" }}
          >
            {error}
            <button
              type="button"
              onClick={fetchClients}
              style={{
                marginLeft: "12px",
                border: "1px solid #DC2626",
                background: "transparent",
                color: "#DC2626",
                borderRadius: "6px",
                padding: "4px 12px",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        ) : null}

        <CustomMRTTable
          columns={columns}
          data={clients}
          rowCount={totalClients}
          loading={loading}
          manualPagination
          manualFiltering
          pagination={pagination}
          onPaginationChange={setPagination}
          enablePagination
          enableSorting
          enableSearch
          searchInput={searchInput}
          setSearchInput={setSearchInput}
          onSearchEnter={handleSearchEnter}
          enableColumnFilters={false}
          enableColumnVisibility={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          enableColumnActions={false}
          enableStickyHeader
          placeholder="Search by Client ID, School Name"
        />
      </div>
    </div>
  );
};

export default School;
